<template>
  <div class="quarter-picker">
    <el-popover
      v-model:visible="popoverVisible"
      placement="bottom-start"
      :width="280"
      trigger="click"
    >
      <template #reference>
        <div class="picker-input" :class="{ 'is-focus': popoverVisible }">
          <input
            type="text"
            readonly
            :value="displayValue"
            :placeholder="placeholder"
            class="el-input__inner"
          />
          <el-icon class="picker-icon"><Calendar /></el-icon>
        </div>
      </template>
      
      <div class="quarter-panel">
        <div class="quarter-header">
          <el-button text @click="prevYear">
            <el-icon><ArrowLeft /></el-icon>
          </el-button>
          <span class="year-text">{{ currentYear }}年</span>
          <el-button text @click="nextYear">
            <el-icon><ArrowRight /></el-icon>
          </el-button>
        </div>
        
        <div class="quarter-body">
          <div class="quarter-grid">
            <div
              v-for="q in 4"
              :key="q"
              class="quarter-item"
              :class="{ 'is-active': isSelected(currentYear, q) }"
              @click="selectQuarter(currentYear, q)"
            >
              Q{{ q }}
            </div>
          </div>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { Calendar, ArrowLeft, ArrowRight } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '选择季度'
  }
})

const emit = defineEmits(['update:modelValue'])

const popoverVisible = ref(false)
const currentYear = ref(new Date().getFullYear())

const displayValue = computed(() => {
  if (!props.modelValue) return ''
  const match = props.modelValue.match(/(\d{4})-Q(\d)/)
  if (match) {
    return `${match[1]}年Q${match[2]}`
  }
  return props.modelValue
})

const isSelected = (year, quarter) => {
  return props.modelValue === `${year}-Q${quarter}`
}

const selectQuarter = (year, quarter) => {
  const value = `${year}-Q${quarter}`
  emit('update:modelValue', value)
  popoverVisible.value = false
}

const prevYear = () => {
  currentYear.value--
}

const nextYear = () => {
  currentYear.value++
}

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    const match = newVal.match(/(\d{4})-Q(\d)/)
    if (match) {
      currentYear.value = parseInt(match[1])
    }
  }
}, { immediate: true })
</script>

<style lang="scss" scoped>
.quarter-picker {
  width: 100%;
}

.picker-input {
  display: flex;
  align-items: center;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 0 12px;
  height: 32px;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s;
  
  &:hover {
    border-color: #c0c4cc;
  }
  
  &.is-focus {
    border-color: #409eff;
  }
  
  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    color: #606266;
    cursor: pointer;
    
    &::placeholder {
      color: #c0c4cc;
    }
  }
  
  .picker-icon {
    color: #c0c4cc;
    font-size: 14px;
  }
}

.quarter-panel {
  .quarter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    border-bottom: 1px solid #ebeef5;
    
    .year-text {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }
  }
  
  .quarter-body {
    padding: 12px;
  }
  
  .quarter-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  
  .quarter-item {
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    color: #606266;
    background: #f5f7fa;
    transition: all 0.2s;
    
    &:hover {
      color: #409eff;
      background: #ecf5ff;
    }
    
    &.is-active {
      color: #fff;
      background: #409eff;
      
      &:hover {
        background: #66b1ff;
      }
    }
  }
}
</style>